import { useState, useMemo, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  postUploadImages,
  getImages,
  deleteImageById,
  postUploadDocuments,
  getDocuments,
  deleteDocumentById,
  getPackage,
  getAllowedFileTypes
} from 'proposals/api/documentServiceApiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

type uploadRequestType = {
  contextToken: string
  data: FormData
}

type DeleteRequestType = {
  contextToken: string
  id: string
}

interface UseFilesReturnType {
  operations: {
    addDropzoneFiles: (files: File[]) => void
    deleteImage: (id: string) => void
    deleteDocument: (id: string) => void
    getArchive: () => void
  }
  models: {
    images: ImageReturnType[]
    imagesUploading: ImageFileType[]
    documents: DocumentReturnType[]
    documentsUploading: File[]
    isImagesLoading: boolean
    showUploadError: boolean
    isDocumentsLoading: boolean
    isArchiveLoading: boolean
    inputAcceptFileTypes: string
  }
}

const isFileImage = (file: File): boolean => file.type.split('/')[0] === 'image'

export const useAttachments = (contextToken: string): UseFilesReturnType => {
  const [imagesUploading, setImagesUploading] = useState<ImageFileType[]>([])
  const [documentsUploading, setDocumentUploading] = useState<File[]>([])
  const [showUploadError, setShowUploadError] = useState<boolean>(false)

  const queryClient = useQueryClient()

  const { data: imagesData = [], isLoading: isImagesLoading } = useQuery({
    queryKey: ['attachments-images', contextToken],
    queryFn: () => getImages(contextToken),
    refetchOnWindowFocus: false,
    enabled: !!contextToken
  })

  const { data: documentsData = [], isLoading: isDocumentsLoading } = useQuery({
    queryKey: ['attachments-documents', contextToken],
    queryFn: () => getDocuments(contextToken),
    refetchOnWindowFocus: false,
    enabled: !!contextToken
  })

  const { mutate: getArchive, isPending: isArchiveLoading } = useMutation({
    mutationFn: () => getPackage(contextToken),
    onSuccess: (urlToBlob) => {
      const link = document.createElement('a')
      link.href = urlToBlob
      link.setAttribute('download', 'Liitteet.zip')
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
    }
  })

  const { data: allowedFileTypesData = [] } = useQuery({
    queryKey: ['allowed-file-types'],
    queryFn: () => getAllowedFileTypes(contextToken),
    refetchOnWindowFocus: false,
    enabled: !!contextToken,
    gcTime: Infinity
  })

  const { mutate: uploadImages, isError: uploadImageError } = useMutation({
    mutationFn: (imageRequest: uploadRequestType) => {
      return postUploadImages(imageRequest.data, imageRequest.contextToken)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        ['attachments-images', contextToken],
        (oldData: ImageReturnType[] = []): ImageReturnType[] => {
          return [...oldData, ...data]
        }
      )
      setImagesUploading(
        imagesUploading.filter((uploadingImage) => !data.find((item) => item.originalName.normalize() === uploadingImage.name.normalize()))
      )
    },
    onError: () => {
      setImagesUploading([])
    }
  })

  const { mutate: deleteImage } = useMutation({
    mutationFn: (deleteImageRequest: DeleteRequestType) => deleteImageById(deleteImageRequest.id, deleteImageRequest.contextToken),
    onMutate: (deleteImageRequest: DeleteRequestType) => {
      const oldImages = queryClient.getQueryData<ImageReturnType[]>(['attachments-images', contextToken])

      queryClient.setQueryData(
        ['attachments-images', contextToken],
        (oldData: ImageReturnType[] = []): ImageReturnType[] => {
          return oldData.filter((item) => item.id !== deleteImageRequest.id)
        }
      )

      return { oldImages }
    },
    onError: (context: any) => {
      if (context?.oldImages) {
        queryClient.setQueryData<ImageReturnType[]>(['attachments-images', contextToken], context.oldImages)
      }
    }
  })

  const { mutate: deleteDocument } = useMutation({
    mutationFn: (deleteDocumentRequest: DeleteRequestType) => {
      return deleteDocumentById(deleteDocumentRequest.id, deleteDocumentRequest.contextToken)
    },
    onMutate: (deleteDocumentRequest: DeleteRequestType) => {
      const oldDocuments = queryClient.getQueryData<DocumentReturnType[]>(['attachments-documents', contextToken])

      queryClient.setQueryData(
        ['attachments-documents', contextToken],
        (oldData: DocumentReturnType[] = []): DocumentReturnType[] => {
          return oldData.filter((item) => item.id !== deleteDocumentRequest.id)
        }
      )

      return { oldDocuments }
    },
    onError: (context: any) => {
      if (context?.oldDocuments) {
        queryClient.setQueryData<DocumentReturnType[]>(['attachments-documents', contextToken], context.oldDocuments)
      }
    }
  })

  const { mutate: uploadDocuments, isError: uploadDocumentError } = useMutation({
    mutationFn: (documentRequest: uploadRequestType) => {
      return postUploadDocuments(documentRequest.data, documentRequest.contextToken)
    },
    onSuccess: (data: any) => {
      queryClient.setQueryData(
        ['attachments-documents', contextToken],
        (oldData: DocumentReturnType[] = []): DocumentReturnType[] => {
          return [...oldData, ...data]
        }
      )
      setDocumentUploading(
        documentsUploading.filter((uploadingImage) => !data.find((item: any) => item.originalName.normalize() === uploadingImage.name.normalize()))
      )
    },
    onError: () => {
      setDocumentUploading([])
    }
  })

  const handleUploadImages = (images: ImageFileType[]): void => {
    if (images.length === 0) {
      return
    }

    const data = new FormData()

    images.forEach((image) => {
      data.append('files', image)
    })

    uploadImages({ data, contextToken })
  }

  const handleUploadDocuments = (documents: File[]): void => {
    if (documents.length === 0) {
      return
    }

    const data = new FormData()

    documents.forEach((file) => {
      data.append('files', file)
    })

    uploadDocuments({ data, contextToken })
  }

  const addDropzoneFiles = (files: File[]): void => {
    if (files) {
      const imageFiles = files.filter(isFileImage)
      const documentFiles = files.filter((item: File) => !isFileImage(item))

      const newImages: ImageFileType[] = imageFiles.map((file: File) =>
        Object.assign(file, { id: uuidv4(), url: URL.createObjectURL(file) })
      )

      if (newImages.length > 0) {
        handleUploadImages(newImages)
        setImagesUploading([...imagesUploading, ...newImages])
      }

      if (documentFiles.length > 0) {
        handleUploadDocuments(documentFiles)
        setDocumentUploading([...documentsUploading, ...documentFiles])
      }
    }
  }

  const handleDeleteImage = (id: string): void => {
    deleteImage({
      id,
      contextToken
    })
  }

  const handleDeleteDocument = (id: string): void => {
    deleteDocument({
      id,
      contextToken
    })
  }

  const inputAcceptFileTypes = useMemo(() => {
    const extensionTypes = allowedFileTypesData.map((extension) => '.' + extension.extension).join(', ')
    return extensionTypes
  }, [allowedFileTypesData])

  useEffect(() => {
    if (uploadDocumentError || uploadImageError) {
      setShowUploadError(true)
      setTimeout((): void => {
        setShowUploadError(false)
      }, 8000)
    }
  }, [uploadDocumentError, uploadImageError])

  return {
    operations: {
      addDropzoneFiles,
      deleteImage: handleDeleteImage,
      deleteDocument: handleDeleteDocument,
      getArchive
    },
    models: {
      images: imagesData.sort((a, b) => a.ordering - b.ordering),
      imagesUploading,
      documents: documentsData.sort((a, b) => a.ordering - b.ordering),
      documentsUploading,
      isImagesLoading,
      showUploadError,
      isDocumentsLoading,
      isArchiveLoading,
      inputAcceptFileTypes
    }
  }
}
